// components/UI.jsx
// All small reusable components in one file

import { useState } from "react";

// ── Card ──────────────────────────────────────────────────────
export function Card({ children, style = {}, className = "", onClick }) {
  return (
    <div
      className={`dp-card dp-fade-in ${onClick ? "dp-card-click" : ""} ${className}`}
      onClick={onClick}
      style={{ padding: "22px 24px", transition: "all 0.15s", ...style }}
    >
      {children}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────
export function StatCard({ label, value, sub, color, icon, onClick }) {
  return (
    <div
      onClick={onClick}
      className={onClick ? "dp-card-click" : "dp-card"}
      style={{
        background: "#fff", borderRadius: 14,
        padding: "18px 22px",
        borderLeft: `4px solid ${color}`,
        flex: 1, minWidth: 150,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <p style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, margin: 0 }}>
          {label}
        </p>
        {icon && <span style={{ fontSize: 18, opacity: 0.6 }}>{icon}</span>}
      </div>
      <h3 style={{
        fontSize: 26, fontWeight: 800, color: "#0f172a",
        fontFamily: "'DM Mono', monospace", letterSpacing: -0.5,
        margin: "0 0 5px", lineHeight: 1,
      }}>
        {value}
      </h3>
      <p style={{ fontSize: 12, color, fontWeight: 600, margin: 0 }}>{sub}</p>
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────
export function Toggle({ on, onToggle, disabled = false }) {
  return (
    <div
      onClick={disabled ? undefined : onToggle}
      style={{
        width: 46, height: 26, borderRadius: 13,
        cursor: disabled ? "not-allowed" : "pointer",
        background: on ? "#2563eb" : "#cbd5e1",
        position: "relative", transition: "background 0.2s",
        flexShrink: 0, opacity: disabled ? 0.5 : 1,
        boxShadow: on ? "inset 0 1px 3px rgba(0,0,0,0.2)" : "none",
      }}
    >
      <div style={{
        position: "absolute", top: 3,
        left: on ? 23 : 3,
        width: 20, height: 20, borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "left 0.2s",
      }} />
    </div>
  );
}

// ── PageHeader ────────────────────────────────────────────────
export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      alignItems: "flex-start", marginBottom: 28,
    }}>
      <div>
        <h4 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 24, fontWeight: 700,
          color: "#0f172a", margin: 0, lineHeight: 1.2,
        }}>
          {title}
        </h4>
        {subtitle && (
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748b", fontWeight: 400 }}>
            {subtitle}
          </p>
        )}
      </div>
      {children && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0, marginLeft: 20 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── OccupancyChart ────────────────────────────────────────────
export function OccupancyChart({ data }) {
  const max = Math.max(...data.map(d => d.v));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 100 }}>
        {data.map((d, i) => {
          const h   = Math.max((d.v / max) * 100, 4);
          const col = d.v >= 80 ? "#16a34a" : d.v >= 60 ? "#d97706" : "#cbd5e1";
          return (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <span style={{ fontSize: 7, color: "#94a3b8", fontFamily: "'DM Mono',monospace" }}>{d.v}%</span>
              <div
                title={`${d.d}: ${d.v}%`}
                style={{ width: "100%", height: h, background: col, borderRadius: "3px 3px 0 0", transition: "height 0.4s ease" }}
              />
              <span style={{
                fontSize: 7, color: "#94a3b8", whiteSpace: "nowrap",
                transform: "rotate(-40deg)", transformOrigin: "top left",
                marginLeft: 4, marginTop: 8, display: "block",
              }}>{d.d}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 28, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
        {[["#16a34a", "High ≥80%"], ["#d97706", "Med 60–79%"], ["#cbd5e1", "Low <60%"]].map(([c, l]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ConfirmModal ──────────────────────────────────────────────
export function ConfirmModal({ show, title, message, onConfirm, onCancel, confirmLabel = "Confirm", confirmClass = "dp-btn dp-btn-success" }) {
  if (!show) return null;
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(15,23,42,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9998, backdropFilter: "blur(2px)",
    }}>
      <div className="dp-card dp-fade-in" style={{ maxWidth: 440, width: "90%", padding: "32px 36px" }}>
        <h5 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#0f172a", margin: "0 0 12px" }}>
          {title}
        </h5>
        <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.7, margin: "0 0 28px" }}
          dangerouslySetInnerHTML={{ __html: message }}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <button className={confirmClass} onClick={onConfirm} style={{ flex: 1 }}>{confirmLabel}</button>
          <button className="dp-btn dp-btn-ghost" onClick={onCancel} style={{ flex: 1 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── SectionTitle ──────────────────────────────────────────────
export function SectionTitle({ title, sub, action }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: sub ? 3 : 0 }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: "#64748b" }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

// ── StatusRow ─────────────────────────────────────────────────
export function StatusRow({ label, value, ok }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #f1f5f9" }}>
      <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: ok === true ? "#15803d" : ok === false ? "#b91c1c" : "#b45309", fontFamily: "'DM Mono',monospace" }}>
        {ok === true ? "● " : ok === false ? "✕ " : "⚠ "}{value}
      </span>
    </div>
  );
}

// ── CodeBlock ─────────────────────────────────────────────────
export function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      {label && <div className="dp-label" style={{ marginBottom: 8 }}>{label}</div>}
      <div style={{ position: "relative" }}>
        <pre style={{
          background: "#0f172a", color: "#e2e8f0",
          borderRadius: 10, padding: "18px 18px",
          fontSize: 11, lineHeight: 1.85,
          overflow: "auto", margin: 0, maxHeight: 340,
          fontFamily: "'DM Mono', monospace",
        }}>
          {code}
        </pre>
        <button
          onClick={handleCopy}
          style={{
            position: "absolute", top: 10, right: 10,
            padding: "4px 12px",
            background: copied ? "#16a34a" : "#334155",
            color: "#fff", border: "none", borderRadius: 6,
            cursor: "pointer", fontSize: 11, fontWeight: 700,
            transition: "background 0.2s",
          }}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
